import { SyntheticEvent } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import Slide, { SlideProps } from '@mui/material/Slide';
import MuiSnackbar from '@mui/material/Snackbar';
import { alpha, useTheme } from '@mui/material/styles';

// project imports
import IconButton from './IconButton';
import { closeSnackbar, useGetSnackbar } from 'api/snackbar';

// assets
import CloseOutlined from '@ant-design/icons/CloseOutlined';

// types
import { KeyedObject } from 'types/root';

// animation function
function TransitionSlideLeft(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

function TransitionSlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

function TransitionSlideRight(props: SlideProps) {
  return <Slide {...props} direction="right" />;
}

function TransitionSlideDown(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

function GrowTransition(props: SlideProps) {
  return <Grow {...props} />;
}

// animation options
const animation: KeyedObject = {
  SlideLeft: TransitionSlideLeft,
  SlideUp: TransitionSlideUp,
  SlideRight: TransitionSlideRight,
  SlideDown: TransitionSlideDown,
  Grow: GrowTransition,
  Fade
};

// ==============================|| SNACKBAR ||============================== //

export default function Snackbar() {
  const { snackbar } = useGetSnackbar();
  const theme = useTheme();
  const resolvedSeverity = ['error', 'warning', 'success', 'info'].includes(snackbar.alert.color ?? '')
    ? (snackbar.alert.color as 'error' | 'info' | 'success' | 'warning')
    : 'info';
  const severityPalette = theme.palette[resolvedSeverity];

  const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    closeSnackbar();
  };

  return (
    <>
      {/* default snackbar */}
      {snackbar.variant === 'default' && (
        <MuiSnackbar
          anchorOrigin={snackbar.anchorOrigin}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleClose}
          message={snackbar.message}
          slots={{ transition: animation[snackbar.transition] }}
          action={
            <>
              <Button  onClick={handleClose}>
                UNDO
              </Button>
              <IconButton  aria-label="close" color="inherit" onClick={handleClose} sx={{ mt: 0.25 }}>
                <CloseOutlined />
              </IconButton>
            </>
          }
        />
      )}
      {/* alert snackbar */}
      {snackbar.variant === 'alert' && (
        <MuiSnackbar
          anchorOrigin={snackbar.anchorOrigin}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleClose}
          slots={{ transition: animation[snackbar.transition] }}
        >
          <Alert
            variant={snackbar.alert.variant}
            severity={resolvedSeverity}
            color={resolvedSeverity}
            action={
              <>
                {snackbar.actionButton !== false && (
                  <>
                    <Button color={snackbar.alert.color ?? 'info'} onClick={handleClose}>
                      UNDO
                    </Button>
                    <IconButton
                      sx={{ mt: 0.25 }}
                      aria-label="close"
                      variant="contained"
                      color={snackbar.alert.color ?? 'info'}
                      onClick={handleClose}
                    >
                      <CloseOutlined />
                    </IconButton>
                  </>
                )}
                {snackbar.actionButton === false && snackbar.close && (
                  <IconButton
                    sx={{ mt: 0.25, '&:hover': { bgcolor: 'transparent' } }}
                    aria-label="close"
                    variant="contained"
                    color={snackbar.alert.color ?? 'info'}
                    onClick={handleClose}
                  >
                    <CloseOutlined />
                  </IconButton>
                )}
              </>
            }
            sx={{
              minWidth: { xs: 280, sm: 420 },
              borderRadius: 2,
              border: `1px solid ${alpha(severityPalette.main, 0.7)}`,
              boxShadow: `0 8px 24px ${alpha(severityPalette.main, 0.28)}`,
              '& .MuiAlert-icon': { color: severityPalette.main },
              ...(snackbar.alert.variant === 'filled'
                ? {
                    bgcolor: alpha(severityPalette.main, theme.palette.mode === 'dark' ? 0.24 : 0.16),
                    color: theme.palette.getContrastText(severityPalette.main)
                  }
                : {
                    bgcolor: alpha(severityPalette.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                    color: theme.palette.text.primary
                  }),
              ...(snackbar.alert.variant === 'outlined' && { bgcolor: alpha(severityPalette.main, 0.06) })
            }}
          >
            {snackbar.message}
          </Alert>
        </MuiSnackbar>
      )}
    </>
  );
}
